import IconDelete from '@assets/icons/delete.svg?react';
import IconDefault from '@assets/icons/default.svg?react';
import IconDuplicate from '@assets/icons/duplicate.svg?react';
import IconEdit from '@assets/icons/edit.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import IconSave from '@assets/icons/save.svg?react';
import {
 Box,
 Card,
 CardContent,
 CircularProgress,
 IconButton,
 Typography,
 useTheme,
} from '@mui/material';
import { AccessGroupsDTO } from '@redux/identity/identityTypes';
import { useTranslation } from 'react-i18next';

interface GroupsListProps {
 accessGroups: AccessGroupsDTO[];
 selectedGroupId: string | null;
 isLoadingList: boolean;
 isApplyingRoleChanges: boolean;
 hasRoleChanges: boolean;
 handleSelectGroup: (group: AccessGroupsDTO) => void;
 handleOpenCreateModal: () => void;
 handleDeleteGroup: () => void;
 handleOpenEditModal: () => void;
 handleCopyGroup: () => void;
 handleResetRoleChanges: () => void;
 handleSaveRoleChanges: () => void;
}

const GroupsList = ({
 accessGroups,
 selectedGroupId,
 isLoadingList,
 isApplyingRoleChanges,
 hasRoleChanges,
 handleSelectGroup,
 handleOpenCreateModal,
 handleDeleteGroup,
 handleOpenEditModal,
 handleCopyGroup,
 handleResetRoleChanges,
 handleSaveRoleChanges,
}: GroupsListProps) => {
 const { t } = useTranslation();
 const th = useTheme();

 return (
  <Box
   sx={{
    mb: { xs: 2, md: 0 },
    order: { xs: 1, md: 2 },
    height: '100%',
    width: { xs: '100%', md: '320px' },
    minWidth: { md: '320px' },
    transition: 'all 0.2s ease-in-out',
    marginLeft: { xs: '0px', md: '16px' },
    position: 'relative',
   }}
  >
   <Card sx={{ minHeight: '100%', height: '100%' }}>
    <CardContent
     sx={{
      position: 'relative',
      p: '20px',
      fontSize: '12px',
      flex: 1,
      height: '100%',
      minHeight: 0,
      '&:last-child': { pb: 3 },
      display: 'flex',
      flexDirection: 'column',
     }}
    >
     <Box
      sx={{
       position: 'sticky',
       top: 0,
       zIndex: 1,
       display: 'flex',
       justifyContent: 'space-between',
       alignItems: 'center',
       mb: '14px',
      }}
     >
      <Typography
       sx={{
        fontSize: { xs: 14, md: 16 },
        fontWeight: 500,
       }}
      >
       {t('accessGroups.title')}
      </Typography>
      <IconButton
       aria-label="create access group"
       onClick={handleOpenCreateModal}
      >
       <IconPlus style={{ color: th.palette.kemek.blue }} />
      </IconButton>
     </Box>

     {isLoadingList && (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
       <CircularProgress size={24} />
      </Box>
     )}

     <Box
      sx={{
       borderRadius: '4px',
       border: `1px solid ${th.palette.kemek.lightestGray}`,
       mb: '12px',

       flex: 1,
       minHeight: 0,
       overflowY: 'auto',
      }}
     >
      {accessGroups.map((group) => (
       <Box
        key={group.Id || 'no-id'}
        onClick={() => handleSelectGroup(group)}
        tabIndex={0}
        role="button"
        aria-label={group.Name || ''}
        onKeyDown={(event) => {
         if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSelectGroup(group);
         }
        }}
        sx={{
         cursor: 'pointer',
         fontSize: 12,
         transition: 'background-color 0.3s ease-in-out',
         backgroundColor:
          group.Id === selectedGroupId
           ? th.palette.kemek.lightBlue
           : 'transparent',
         p: '12px',
         borderBottom: `1px solid ${th.palette.kemek.lightestGray}`,
         '&:last-child': { borderBottom: 0 },
         '&:hover': {
          backgroundColor:
           group.Id === selectedGroupId
            ? th.palette.kemek.bg1
            : th.palette.action.hover,
         },
        }}
       >
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
         {group.Name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: th.palette.kemek.darkGray }}>
         {group.Description}
        </Typography>
       </Box>
      ))}
     </Box>

     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <IconButton
       aria-label="delete access group"
       disabled={!selectedGroupId}
       onClick={handleDeleteGroup}
       sx={{ opacity: !selectedGroupId ? 0.5 : 1 }}
      >
       <IconDelete
        style={{ color: th.palette.kemek[!selectedGroupId ? 'gray' : 'red'] }}
       />
      </IconButton>
      <IconButton
       aria-label="edit access group"
       disabled={!selectedGroupId}
       onClick={handleOpenEditModal}
       sx={{ opacity: !selectedGroupId ? 0.5 : 1 }}
      >
       <IconEdit
        style={{
         color: th.palette.kemek[!selectedGroupId ? 'gray' : 'darkestGray'],
        }}
       />
      </IconButton>
      <IconButton
       aria-label="copy access group"
       disabled={!selectedGroupId || isApplyingRoleChanges}
       onClick={handleCopyGroup}
       sx={{ opacity: !selectedGroupId || isApplyingRoleChanges ? 0.5 : 1 }}
      >
       <IconDuplicate
        style={{
         color:
          th.palette.kemek[
           !selectedGroupId || isApplyingRoleChanges ? 'gray' : 'darkestGray'
          ],
        }}
       />
      </IconButton>
      <IconButton
       aria-label="reset access group roles"
       disabled={!selectedGroupId || !hasRoleChanges || isApplyingRoleChanges}
       onClick={handleResetRoleChanges}
       sx={{
        ml: 'auto',
        opacity:
         !selectedGroupId || !hasRoleChanges || isApplyingRoleChanges ? 0.5 : 1,
       }}
      >
       <IconDefault
        style={{
         color:
          th.palette.kemek[
           !selectedGroupId || !hasRoleChanges || isApplyingRoleChanges
            ? 'gray'
            : 'darkestGray'
          ],
        }}
       />
      </IconButton>
      <IconButton
       aria-label="save access group roles"
       disabled={!selectedGroupId || !hasRoleChanges || isApplyingRoleChanges}
       onClick={handleSaveRoleChanges}
       sx={{
        opacity:
         !selectedGroupId || !hasRoleChanges || isApplyingRoleChanges ? 0.5 : 1,
       }}
      >
       <IconSave
        style={{
         color:
          th.palette.kemek[
           !selectedGroupId || !hasRoleChanges || isApplyingRoleChanges
            ? 'gray'
            : 'blue'
          ],
        }}
       />
      </IconButton>
     </Box>
    </CardContent>
   </Card>
  </Box>
 );
};

export default GroupsList;
